export enum FlnCategory {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  GRADUATE = 'GRADUATE',
}

export interface ICheckPoint {
  id: number;
  name: string;
  grades: number[];
  assessmentStartTime: string | null;
  assessmentEndTime: string | null;
  isDisabled?: boolean;
  disabledReason?: string | null;
}

export interface ICheckPointResult {
  id: number;
  checkpoint: number;
  position: number;
  checkpointTotalMarks: string;
  totalObtainedMarks: string;
  student: number;
  percentage: number;
}

export interface IUser {
  additionalPhoneNumbers?: string[];
  id?: number;
  isUsernameDummyPhone?: boolean;
  name?: string | null;
  username?: string | null;
  uuid?: string;
  isNewCreated?: boolean;
  hasStaffProfile?: boolean;
}

export interface IStudentResponse {
  id?: number;
  name?: string;
  schoolClass?: number;
  admissionNumber?: string;
  bFormNumber?: string | null;
  verificationStatus?: string;
  user?: number;
}

export interface IStudentData {
  id?: number;
  name?: string;
  schoolClass?: number;
  admissionNumber?: string | null;
  bFormNumber?: string | null;
  verificationStatus?: string;
  userId?: number;
  uuid?: string;
  isNewCreated?: boolean | false;
  inviteStatus?: boolean | null;
  inviteSentAt?: string | null;
  totalExamImagesSubmitted?: number;
  username?: string | null;
  status?: SessionStatus | null;
  session?: string | null;
  statusChange?: boolean | null;
  category?: FlnCategory | null;
}

export interface IAssessments {
  id: number;
  checkpoint: number;
  totalMarks: string;
  gradeSubject: number;
  schoolClass: number;
}

export interface IAssessmentResult {
  id: number;
  assessment: number;
  student: number;
  obtainedMarks: string;
  aiRemarks: string;
}

export enum VerificationStatus {
  Verified = 'VERIFIED',
  NotVerified = 'NOT VERIFIED',
}

export enum VerificationStatusText {
  Verified = 'Verified',
  NotVerified = 'Unverified',
  MarkAsVerified = 'Mark as Verified',
}

export enum VerificationStatusIcon {
  Verified = 'check',
  NotVerified = '',
}

export interface InsertStudentData {
  name?: string;
  userId?: number;
  uuid?: string;
  schoolClass?: number;
  verificationStatus?: VerificationStatus;
  isNewCreated?: boolean;
  username: string | null;
  gradeId?: number;
  admissionNumber: string | null;
  status?: SessionStatus | null;
}

export interface AddStudentData {
  name: string;
  schoolClass: number;
  verificationStatus: VerificationStatus;
  isNewCreated: boolean;
  username?: string | null;
  gradeId: number;
  parentName: string | null;
  admissionNumber: string | null;
  isUsernameDummyPhone?: boolean | false;
  additionalPhoneNumbers?: string[];
  status?: SessionStatus | null;
}

interface UpdateUser {
  id?: number;
  uuid?: string;
  name?: string | null;
  username?: string | null;
  additionalPhoneNumbers?: string[];
  isDummyPhone?: boolean;
  isNewCreated?: boolean;
  syncStatus?: string;
}

interface GradeSubject {
  gradeLabel?: string;
  schoolClass?: number;
}

export interface UpdateStudentData {
  id?: number;
  uuid?: string;
  name?: string;
  admissionNumber?: string | null;
  gradeSubject?: GradeSubject;
  user: UpdateUser;
  isNewCreated?: boolean;
  syncStatus?: string;
  statusChange?: boolean | null;
  status?: SessionStatus | null;
}

export enum Skills {
  Reading = 'reading',
  Writing = 'writing',
  Listening = 'listening',
  Speaking = 'speaking',
}

export enum BloomLevels {
  Remember = 'remember',
  Understand = 'understand',
  Apply = 'apply',
  Analyze = 'analyze',
  Evaluate = 'evaluate',
  Create = 'create',
}
export enum SessionStatus {
  Promoted = 'PROMOTED',
  Demoted = 'DEMOTED',
  Failed = 'FAILED',
  Enroll = 'ENROLL',
  Migrated = 'MIGRATED',
  SchoolChange = 'SCHOOL CHANGE',
  ClassChange = 'CLASS CHANGE',
  Other = 'OTHER',
}

// this interface is relevant to the report card
export interface QuestionAnswer {
  id?: number | null;
  type?: string;
  marks: string;
  answer: string;
  question: string;
  reasoning: string;
  questionTags?: BloomLevels;
  competency?: Skills;
  obtainedMarks: string;
  teacherMarks?: number;
  groupType?: string | null;
}

interface OptionalInfo {
  age: number | null;
  examDate: string | null;
  rollNumber: string | null;
  teacherName: string | null;
  teacherMarks: string | null;
  isExamHandwritten: string | null;
  unansweredQuestions: string | null;
}

export interface ICheckedStudentExam {
  id: number;
  uuid: string;
  studentName: string | null;
  checkpoint: number;
  totalMarks: string | null;
  obtainedMarks: string | null;
  remarks: string | null;
  questionAnswers: QuestionAnswer[] | null;
  optionalInfo?: OptionalInfo;
  gradeSubject: number;
  schoolClass: number;
  totalCheckpointAcademicDays: number | null;
  daysPresent: number | null;
  classBehaviour: string | null;
}

interface IErrorRecommendation {
  errors: string[];
  recommendations: string[];
}

export interface IAnalysisResponse {
  grammar: IErrorRecommendation;
  sentenceStructure: IErrorRecommendation;
  spelling: IErrorRecommendation;
}

export interface IStudentSubjectAnswerAnalysis {
  analysisResponse: IAnalysisResponse;
  examGenerator: string;
  id: number;
}
export interface IMatchingResultDetails {
  studentName: string;
  totalMarks: string;
  obtainedMarks: string;
  remarks: string;
  questionAnswers: QuestionAnswer[];
  checkpoint: number;
  classBehaviour: string | null;
  daysPresent: number | null;
  gradeSubject: number;
  id: number;
  totalCheckpointAcademicDays: number | null;
  schoolClass: number;
}

export interface IStudentDuplicate {
  id: number;
  name: string | null;
  uuid: string | null;
  schoolClass: number | null;
  admissionNumber: string | null;
  verificationStatus: string | null;
  existingObject: IExistingObject | null;
  failureReason: string | null;
  gradeSubject?: string | null;
}
export interface IExistingObject {
  id: number;
  name: string;
  user: number;
  schoolClass: number;
  admissionNumber: string;
  bFormNumber: null | string;
  verificationStatus: string;
  gradeSubject?: string | null;
}

export interface IFailedTablesData {
  studentProfile: {
    created?: IStudentDuplicate[];
    updated?: IStudentDuplicate[];
    deleted?: IStudentDuplicate[];
  };
  teacherTrainingStatus?: { created?: { id: string }[]; updated?: { id: string }[] };
}

export interface IResponseData {
  failedTablesData: IFailedTablesData;
}
export interface ICheckedStudentExamV2 {
  uuid: string;
  id: number;
  studentName: string | null;
  studentProfile?: string;
  checkpoint: number;
  totalMarks: string | null;
  obtainedMarks: string | null;
  remarks: string | null;
  questionAnswers: QuestionAnswer[] | null;
  optionalInfo?: OptionalInfo;
  gradeSubject: number;
  schoolClass: number;
  totalCheckpointAcademicDays: number | null;
  daysPresent: number | null;
  classBehaviour: string | null;
  studentCategory?: FlnCategory | null;
  user?: number;
  profile?: number;
}
